import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, CardBody, CardHeader, Input, Button, Row, Col, Spinner, Alert } from 'reactstrap';
import { comptabiliteActions } from '../../sagas/comptabiliteSlice';

const SyntheseComptable = () => {
    const dispatch = useDispatch();
    const { syntheseComptable, loading, error } = useSelector(state => state.comptabilite);
    
    const [filters, setFilters] = useState({
        dateDebut: '',
        dateFin: ''
    });

    useEffect(() => {
        // Load initial synthesis with current month
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        
        const initialFilters = {
            dateDebut: firstDay.toISOString().split('T')[0],
            dateFin: lastDay.toISOString().split('T')[0]
        };
        
        setFilters(initialFilters);
        dispatch(comptabiliteActions.getSyntheseComptable(initialFilters));
    }, [dispatch]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSearch = () => {
        dispatch(comptabiliteActions.getSyntheseComptable({
            dateDebut: filters.dateDebut || null,
            dateFin: filters.dateFin || null
        }));
    };

    const formatPrice = (amount) => {
        return new Intl.NumberFormat('fr-DZ', {
            style: 'currency',
            currency: 'DZD'
        }).format(amount || 0);
    };

    const formatPercentage = (value) => {
        return new Intl.NumberFormat('fr-DZ', {
            style: 'percent',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format((value || 0) / 100);
    };

    const exportReport = () => {
        // Implementation for report export
        console.log('Export report functionality to be implemented');
    };

    return (
        <div className="page-content">
            <Card>
                <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                        <h4 className="card-title mb-0">Synthèse Comptable</h4>
                        <Button color="info" onClick={exportReport}>
                            Exporter Rapport
                        </Button>
                    </div>
                </CardHeader>
                <CardBody>
                    {/* Filters */}
                    <Row className="mb-4">
                        <Col md={4}>
                            <Input
                                type="date"
                                placeholder="Date début"
                                value={filters.dateDebut}
                                onChange={(e) => handleFilterChange('dateDebut', e.target.value)}
                            />
                        </Col>
                        <Col md={4}>
                            <Input
                                type="date"
                                placeholder="Date fin"
                                value={filters.dateFin}
                                onChange={(e) => handleFilterChange('dateFin', e.target.value)}
                            />
                        </Col>
                        <Col md={4}>
                            <Button 
                                color="primary" 
                                onClick={handleSearch}
                                disabled={loading.syntheseComptable}
                                block
                            >
                                {loading.syntheseComptable ? <Spinner size="sm" /> : 'Générer Synthèse'}
                            </Button>
                        </Col>
                    </Row>

                    {/* Error Display */}
                    {error && (
                        <Alert color="danger">
                            {error}
                        </Alert>
                    )}

                    {/* Loading */}
                    {loading.syntheseComptable && (
                        <div className="text-center">
                            <Spinner color="primary" />
                        </div>
                    )}

                    {/* Synthesis Display */}
                    {syntheseComptable && Object.keys(syntheseComptable).length > 0 && (
                        <>
                            {/* Period Information */}
                            <Card className="mb-4">
                                <CardBody>
                                    <h5>Période d'analyse</h5>
                                    <Row>
                                        <Col md={6}>
                                            <p><strong>Du:</strong> {filters.dateDebut ? new Date(filters.dateDebut).toLocaleDateString('fr-DZ') : 'Début'}</p>
                                        </Col>
                                        <Col md={6}>
                                            <p><strong>Au:</strong> {filters.dateFin ? new Date(filters.dateFin).toLocaleDateString('fr-DZ') : 'Fin'}</p>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>

                            {/* Key Indicators */}
                            <Row className="mb-4">
                                <Col md={3}>
                                    <Card className="text-center">
                                        <CardBody>
                                            <div className="avatar-sm mx-auto mb-3">
                                                <span className="avatar-title rounded-circle bg-primary">
                                                    <i className="mdi mdi-cash-multiple font-size-16"></i>
                                                </span>
                                            </div>
                                            <h5 className="mb-1">{formatPrice(syntheseComptable.chiffreAffaires || 0)}</h5>
                                            <p className="text-muted mb-0">Chiffre d'Affaires</p>
                                        </CardBody>
                                    </Card>
                                </Col>
                                <Col md={3}>
                                    <Card className="text-center">
                                        <CardBody>
                                            <div className="avatar-sm mx-auto mb-3">
                                                <span className="avatar-title rounded-circle bg-success">
                                                    <i className="mdi mdi-trending-up font-size-16"></i>
                                                </span>
                                            </div>
                                            <h5 className="mb-1">{formatPrice(syntheseComptable.benefice || 0)}</h5>
                                            <p className="text-muted mb-0">Bénéfice</p>
                                        </CardBody>
                                    </Card>
                                </Col>
                                <Col md={3}>
                                    <Card className="text-center">
                                        <CardBody>
                                            <div className="avatar-sm mx-auto mb-3">
                                                <span className="avatar-title rounded-circle bg-warning">
                                                    <i className="mdi mdi-trending-down font-size-16"></i>
                                                </span>
                                            </div>
                                            <h5 className="mb-1">{formatPrice(syntheseComptable.charges || 0)}</h5>
                                            <p className="text-muted mb-0">Total Charges</p>
                                        </CardBody>
                                    </Card>
                                </Col>
                                <Col md={3}>
                                    <Card className="text-center">
                                        <CardBody>
                                            <div className="avatar-sm mx-auto mb-3">
                                                <span className="avatar-title rounded-circle bg-info">
                                                    <i className="mdi mdi-percent font-size-16"></i>
                                                </span>
                                            </div>
                                            <h5 className="mb-1">{formatPercentage(syntheseComptable.margePercentage || 0)}</h5>
                                            <p className="text-muted mb-0">Marge</p>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Financial Structure */}
                            {syntheseComptable.structureFinanciere && (
                                <Card className="mb-4">
                                    <CardHeader>
                                        <h5>Structure Financière</h5>
                                    </CardHeader>
                                    <CardBody>
                                        <Row>
                                            <Col md={6}>
                                                <h6>Actif</h6>
                                                <div className="mb-2">
                                                    <span>Actif Immobilisé</span>
                                                    <span className="float-end">{formatPrice(syntheseComptable.structureFinanciere.actifImmobilise || 0)}</span>
                                                </div>
                                                <div className="mb-2">
                                                    <span>Actif Circulant</span>
                                                    <span className="float-end">{formatPrice(syntheseComptable.structureFinanciere.actifCirculant || 0)}</span>
                                                </div>
                                                <div className="border-top pt-2">
                                                    <strong>
                                                        <span>Total Actif</span>
                                                        <span className="float-end">{formatPrice(syntheseComptable.structureFinanciere.totalActif || 0)}</span>
                                                    </strong>
                                                </div>
                                            </Col>
                                            <Col md={6}>
                                                <h6>Passif</h6>
                                                <div className="mb-2">
                                                    <span>Capitaux Propres</span>
                                                    <span className="float-end">{formatPrice(syntheseComptable.structureFinanciere.capitauxPropres || 0)}</span>
                                                </div>
                                                <div className="mb-2">
                                                    <span>Dettes</span>
                                                    <span className="float-end">{formatPrice(syntheseComptable.structureFinanciere.dettes || 0)}</span>
                                                </div>
                                                <div className="border-top pt-2">
                                                    <strong>
                                                        <span>Total Passif</span>
                                                        <span className="float-end">{formatPrice(syntheseComptable.structureFinanciere.totalPassif || 0)}</span>
                                                    </strong>
                                                </div>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            )}

                            {/* Ratios */}
                            {syntheseComptable.ratios && (
                                <Card className="mb-4">
                                    <CardHeader>
                                        <h5>Ratios Financiers</h5>
                                    </CardHeader>
                                    <CardBody>
                                        <Row>
                                            <Col md={6}>
                                                <h6>Ratios de Liquidité</h6>
                                                <div className="mb-2">
                                                    <span>Ratio de Liquidité Générale</span>
                                                    <span className="float-end">{(syntheseComptable.ratios.liquiditeGenerale || 0).toFixed(2)}</span>
                                                </div>
                                                <div className="mb-2">
                                                    <span>Ratio de Liquidité Immédiate</span>
                                                    <span className="float-end">{(syntheseComptable.ratios.liquiditeImmediate || 0).toFixed(2)}</span>
                                                </div>
                                            </Col>
                                            <Col md={6}>
                                                <h6>Ratios de Rentabilité</h6>
                                                <div className="mb-2">
                                                    <span>ROE (Return on Equity)</span>
                                                    <span className="float-end">{formatPercentage(syntheseComptable.ratios.roe || 0)}</span>
                                                </div>
                                                <div className="mb-2">
                                                    <span>ROA (Return on Assets)</span>
                                                    <span className="float-end">{formatPercentage(syntheseComptable.ratios.roa || 0)}</span>
                                                </div>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            )}

                            {/* Top Accounts */}
                            {syntheseComptable.topComptes && (
                                <Card>
                                    <CardHeader>
                                        <h5>Comptes les Plus Actifs</h5>
                                    </CardHeader>
                                    <CardBody>
                                        <Row>
                                            {syntheseComptable.topComptes.map((compte, index) => (
                                                <Col md={4} key={index} className="mb-3">
                                                    <Card className="border">
                                                        <CardBody className="py-3">
                                                            <div className="d-flex align-items-center">
                                                                <div className="avatar-xs me-3">
                                                                    <span className="avatar-title rounded-circle bg-soft-primary text-primary">
                                                                        {index + 1}
                                                                    </span>
                                                                </div>
                                                                <div className="flex-1">
                                                                    <h6 className="mb-1">{compte.numeroCompte}</h6>
                                                                    <p className="text-muted mb-0 small">{compte.intituleCompte}</p>
                                                                    <small className="text-success">{formatPrice(compte.montantTotal)}</small>
                                                                </div>
                                                            </div>
                                                        </CardBody>
                                                    </Card>
                                                </Col>
                                            ))}
                                        </Row>
                                    </CardBody>
                                </Card>
                            )}
                        </>
                    )}

                    {/* Empty State */}
                    {syntheseComptable && Object.keys(syntheseComptable).length === 0 && !loading.syntheseComptable && (
                        <div className="text-center py-4">
                            <h5>Aucune donnée disponible</h5>
                            <p className="text-muted">Aucune donnée comptable trouvée pour la période sélectionnée.</p>
                        </div>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default SyntheseComptable;
